import { useCallback, useRef, useState } from "react"
import { UseFormReturn } from "react-hook-form"
import { isBetterDocAvailable } from "@/modules/patients/detail/better-doc/lib/is-better-doc-available"
import { isBetterDocRequired } from "@/modules/patients/detail/better-doc/lib/is-better-doc-required"
import { PointOfCareFormType } from "@/modules/patients/detail/progress-note/point-of-care/schema"
import { useConfirmBetterDocRejectionMutation } from "@/modules/patients/services/useConfirmBetterDocRejectionMutation"
import { BetterDoc, BetterDocRejectionReason, Country } from "@/types/__generated__/union-types"

type UseBetterDocRejectionConfirmationParameters = {
  form: UseFormReturn<PointOfCareFormType>
  countryCode: Country
  betterDocUsage: BetterDoc | null
  isBetterDocUsagePending: boolean
  progressNoteId: string
  confirmedBetterDocRejectionReason: BetterDocRejectionReason | null | undefined
}

export type CareProviderSearchGuard = (
  openCareProviderSearch: VoidFunction,
  event: React.FocusEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
) => void

type UseBetterDocRejectionConfirmationResult = {
  guardCareProviderSearch: CareProviderSearchGuard
  isRejectionDialogOpen: boolean
  isConfirmingBetterDocRejection: boolean
  handleRejectionCancel: () => void
  handleRejectionConfirm: (betterDocRejectionReason: BetterDocRejectionReason) => void
  handleRejectionDialogCloseAutoFocus: (event: Event) => void
}

export const useBetterDocRejectionConfirmation = ({
  form,
  countryCode,
  betterDocUsage,
  isBetterDocUsagePending,
  progressNoteId,
  confirmedBetterDocRejectionReason,
}: UseBetterDocRejectionConfirmationParameters): UseBetterDocRejectionConfirmationResult => {
  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false)
  const careProviderFieldRef = useRef<HTMLElement | null>(null)
  const openCareProviderSearchRef = useRef<VoidFunction | null>(null)

  const { getValues } = form
  const { confirmBetterDocRejection, isConfirmingBetterDocRejection } =
    useConfirmBetterDocRejectionMutation(countryCode)
  const isBetterDocApplicable = isBetterDocAvailable(countryCode)

  const guardCareProviderSearch = useCallback<CareProviderSearchGuard>(
    (openCareProviderSearch, event): void => {
      if (isBetterDocApplicable && isBetterDocUsagePending) {
        event.preventDefault()
        const { currentTarget } = event
        currentTarget.blur()
        return
      }

      const isRejectionReasonMissing =
        !confirmedBetterDocRejectionReason && getValues("betterDocSuggestionStatus") !== "MANUAL_SEARCH_NEEDED"
      if (isBetterDocApplicable && isBetterDocRequired(betterDocUsage) && isRejectionReasonMissing) {
        event.preventDefault()
        const { currentTarget } = event
        currentTarget.blur()

        careProviderFieldRef.current = currentTarget
        openCareProviderSearchRef.current = openCareProviderSearch
        setIsRejectionDialogOpen(true)
      } else {
        openCareProviderSearch()
      }
    },
    [betterDocUsage, confirmedBetterDocRejectionReason, getValues, isBetterDocApplicable, isBetterDocUsagePending],
  )

  const handleRejectionCancel = useCallback((): void => {
    setIsRejectionDialogOpen(false)
  }, [])

  const handleRejectionConfirm = useCallback(
    (selectedBetterDocRejectionReason: BetterDocRejectionReason): void => {
      confirmBetterDocRejection(
        { id: progressNoteId, betterDocRejectionReason: selectedBetterDocRejectionReason },
        () => {
          setIsRejectionDialogOpen(false)
        },
      )
    },
    [confirmBetterDocRejection, progressNoteId],
  )

  const handleRejectionDialogCloseAutoFocus = useCallback(
    (event: Event): void => {
      event.preventDefault()
      if (confirmedBetterDocRejectionReason) {
        careProviderFieldRef.current?.focus()
        openCareProviderSearchRef.current?.()
      } else {
        document.getElementById("better-doc-button")?.focus()
      }
    },
    [confirmedBetterDocRejectionReason],
  )

  return {
    guardCareProviderSearch,
    isRejectionDialogOpen,
    isConfirmingBetterDocRejection,
    handleRejectionCancel,
    handleRejectionConfirm,
    handleRejectionDialogCloseAutoFocus,
  }
}
